/**
 * The handful of components every surface shares.
 *
 * Small on purpose. `tokens.css` carries the design language
 * — bands, band-scoped tokens, the component classes — so what belongs in TSX is only the part
 * where getting it wrong would say something untrue: the standing badge, the tier pill, and the
 * async states. Re-expressing the stylesheet as components would be a second definition of the
 * design, and the two would drift.
 */
import type { ReactNode } from "react"
import { registry, type Standing, type Tier } from "./api"
import { LeadInline } from "./leadgate"

/**
 * `reported` vs `verified` — the one distinction the whole product rests on (NN#8).
 *
 * One component, used on all four surfaces, so the two words cannot come to look alike on one of
 * them. Amber is a signal somebody filed; teal has a signed loss event behind it that agitome
 * resolved. They never blur, and nothing anywhere sums them.
 */
export function StandingBadge({ standing }: { standing: Standing }) {
  return (
    <span className={`badge badge--${standing}`}>
      {standing === "verified" ? "Verified" : "Reported"}
    </span>
  )
}

/**
 * A risk tier.
 *
 * `insufficient_data` is rendered as "Insufficient" in its own class rather than at the safe end
 * of the ladder. It is the absence of an assessment — a page that showed it as the good end would
 * be making exactly the claim the tier vocabulary exists to refuse — so it gets a grey pill and,
 * where there is room, a sentence saying so.
 */
export function TierPill({ tier }: { tier: Tier }) {
  if (tier === "insufficient_data") {
    return (
      <span className="tier tier--insufficient" title="Not an assessment — too few counted reports">
        Insufficient
      </span>
    )
  }
  return <span className={`tier tier--${tier}`}>{tier[0]!.toUpperCase() + tier.slice(1)}</span>
}

/** The rules behind a verdict, in the shape every surface prints them. */
export function Rules({ rules }: { rules: { name: string; detail: string }[] }) {
  return (
    <dl className="spec mt-3">
      {rules.map((rule) => (
        <div key={rule.name}>
          <dt>{rule.name}</dt>
          <dd>{rule.detail}</dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * What a page shows while it is waiting, and when it cannot load.
 *
 * The failure state says what went wrong rather than rendering an empty shell. On this product an
 * empty shell is worse than usual: a table with no rows on a risk registry reads as "nothing has
 * been reported", which is a claim about the world rather than about the network.
 */
export function Async<T>({
  state,
  children,
  empty,
}: {
  state: { data: T | null; error: string | null; loading: boolean }
  children: (data: T) => ReactNode
  empty?: ReactNode
}) {
  if (state.loading) return <p className="fine muted">Loading…</p>
  if (state.error) {
    return (
      <div className="well" role="alert">
        <strong>Could not load this.</strong>
        <p className="fine mt-2">{state.error}</p>
        <p className="fine mt-2">
          This is a failure to reach agitome, not a statement about the subject — nothing here
          should be read as "no incidents".
        </p>
      </div>
    )
  }
  if (state.data === null) return <>{empty ?? null}</>
  return <>{children(state.data)}</>
}

/** A number with its label, the way the design system sets one. */
export function Stat({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="card">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </div>
  )
}

/** A horizontal bar row — severity mixes, reason classes, gate dimensions. */
export function Bar({
  label,
  value,
  max,
  sub,
}: {
  label: string
  value: number
  max: number
  sub?: string
}) {
  const width = max > 0 ? Math.round((value / max) * 100) : 0
  return (
    <div className="bars__row">
      <span className="bars__label">
        {label}
        {sub ? <small>{sub}</small> : null}
      </span>
      <div className="bars__track">
        <div className="bars__fill" style={{ width: `${width}%` }} />
      </div>
      <span className="bars__val">{value}</span>
    </div>
  )
}

/**
 * agitome's binding to the shared lead gate.
 *
 * THE IMPLEMENTATION MOVED to `cortex/standards/web-kit/leadgate.tsx`, vendored here as
 * `leadgate.tsx`. There were two of these, one per product, and two implementations of "ask for
 * an email" is two places for the gate rule to be almost right — which is the same shape of
 * mistake that let `analytics.ts` drift into a privacy bug.
 *
 * What stays here is what this product genuinely owns: the transport (agitome's public capture
 * route, not authoxi's leads route — the server forwards), the product name, and the copy.
 *
 * The unsubscribe link is no longer this component's business either: the kit renders whatever
 * the intake returns, and returning it is the server's promise rather than a thing each form
 * remembers to include.
 */
export function Capture({
  source,
  context,
  cta = "Notify me",
  pitch,
}: {
  source: string
  context?: string
  cta?: string
  pitch?: string
}) {
  return (
    <LeadInline
      transport={{
        // agitome's own capture route, which records locally AND forwards to authoxi's leads
        // plane — see app/module/incidents/routes.py::_capture. Posting straight to authoxi from
        // a browser would put a second product's API in this bundle and widen its CORS.
        // Adapted, not widened. agitome's capture route answers `{note}` — it deliberately
        // returns the same body whether or not the address was already held, so it cannot be
        // used to test whether somebody is on the list. The kit wants an unsubscribe URL if
        // there is one; there is not, and `/unsubscribe` is a page rather than a token here.
        submit: async ({ email }) => {
          await registry.capture({ email, source, context, consent_to_updates: true })
          return {}
        },
      }}
      product="agitome"
      source={source}
      subject={context}
      copy={{
        title: pitch ?? "Tell me when this changes",
        body: "We record your address so we can tell you when this changes — and a person reads it.",
        cta,
        done: "Recorded — thank you. A person reads these.",
      }}
      trigger="page"
    />
  )
}
